/**
 * Die Gliederung der Seitenleiste.
 *
 * Sie liegt hier und nicht in `Shell.tsx`, weil zwei Stellen sie brauchen: die
 * Seitenleiste selbst und die Freischaltmeldung ("Neu verfügbar: Peering &
 * IXP"). Die Beschriftungen zweimal zu pflegen wäre die Sorte Doppelung, die
 * genau dann auseinanderläuft, wenn es niemand merkt.
 *
 * Die Reihenfolge folgt der Denkweise eines Betreibers: erst Überblick, dann
 * Kunden und Verkauf, dann Technik von innen (Rechenzentrum) nach außen (Netz),
 * zuletzt Auswertung und Verwaltung.
 */
import type { World } from '@hosting-sim/core';

/**
 * Was ein Abzeichen zum Rechnen braucht.
 *
 * Bewusst nur diese eine Eigenschaft statt des Store-Typs: Andernfalls hinge
 * dieses Modul am Store, der Store hängt an diesem Modul, und der Kreis wäre
 * geschlossen.
 */
export type NavMetrics = { derived: World['derived'] } | null;

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Zahl im Abzeichen, z. B. offene Tickets. */
  badge?: (state: NavMetrics) => number | null;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    title: 'Überblick',
    items: [
      { to: '', label: 'Dashboard', icon: '◧' },
      {
        to: 'diagnostics',
        label: 'Störungen',
        icon: '⚠',
        // Die Zahl ist der eigentliche Zweck: Wer offline geht, sieht es hier
        // sofort, ohne erst suchen zu müssen.
        badge: (s) => s?.derived.diagnostics.filter((d) => d.severity === 'critical' || d.severity === 'error').length || null,
      },
      { to: 'monitoring', label: 'Monitoring', icon: '◔' },
      { to: 'map', label: 'Netzkarte', icon: '⬡' },
      { to: 'world', label: 'Weltlage', icon: '◍' },
      { to: 'news', label: 'Fachpresse', icon: '≣' },
    ],
  },
  {
    title: 'Geschäft',
    items: [
      { to: 'customers', label: 'Kunden', icon: '☰' },
      { to: 'products', label: 'Produkte', icon: '❑' },
      { to: 'invoices', label: 'Rechnungen', icon: '€' },
      { to: 'tickets', label: 'Tickets', icon: '✉', badge: (s) => s?.derived.alerts.filter((a) => a.id === 'ticket-backlog').length || null },
      // Einkauf steht vor dem Markt: erst kaufen, dann vergleichen, mit wem
      // man es zu tun hat.
      { to: 'shop', label: 'Einkauf', icon: '⊡' },
      { to: 'market', label: 'Markt', icon: '⚖' },
      { to: 'offers', label: 'Angebote', icon: '☎' },
      { to: 'finance', label: 'Finanzen', icon: '▤' },
    ],
  },
  {
    title: 'Infrastruktur',
    items: [
      { to: 'zuhause', label: 'Standort', icon: '⌂' },
      { to: 'vor-ort', label: 'Einbau vor Ort', icon: '▦' },
      { to: 'datacenters', label: 'Rechenzentren', icon: '▣' },
      { to: 'servers', label: 'Server', icon: '▥' },
      { to: 'parts', label: 'Ersatzteile', icon: '⌸' },
      { to: 'vps', label: 'VPS', icon: '▦' },
      { to: 'dedicated', label: 'Dedicated', icon: '▧' },
      {
        to: 'migrations',
        label: 'Umzüge',
        icon: '⇢',
        // Die Zahl ist der Grund für den Eintrag: Ein laufender Umzug war
        // bisher nirgends sichtbar, obwohl er stundenlang läuft. Transporte
        // zählen mit – ein Gerät auf der Straße ist genauso ein Umzug, nur
        // dauert er Tage statt Stunden.
        badge: (s) => (s ? s.derived.migrations + (s.derived.transports ?? 0) || null : null),
      },
      { to: 'staff', label: 'Personal', icon: '☗' },
    ],
  },
  {
    title: 'Netzwerk',
    items: [
      { to: 'routers', label: 'Router', icon: '⇄' },
      { to: 'switches', label: 'Switches', icon: '⇅' },
      { to: 'links', label: 'Leitungen', icon: '⟷' },
      { to: 'bgp', label: 'BGP', icon: '⌁' },
      { to: 'ixp', label: 'Peering & IXP', icon: '✳' },
      { to: 'ipam', label: 'IP-Adressen', icon: '⊞' },
      { to: 'loadbalancer', label: 'Loadbalancer', icon: '⚖' },
      { to: 'services', label: 'DNS & Dienste', icon: '⊕' },
    ],
  },
  {
    title: 'Verwaltung',
    items: [
      { to: 'objectives', label: 'Ziele & Erfolge', icon: '◎' },
      { to: 'chronicle', label: 'Chronik', icon: '⏳' },
      { to: 'chat', label: 'Chat', icon: '◌' },
      { to: 'research', label: 'Forschung', icon: '⚛' },
      { to: 'advisor', label: 'KI-Assistent', icon: '✦' },
      { to: 'logs', label: 'Protokoll', icon: '≡' },
      // Das Handbuch steht bewusst in der Seitenleiste und nicht in einer
      // Fußzeile: Fragen entstehen mitten im Spielen, und wer suchen muss,
      // sucht nicht, sondern rät weiter.
      { to: 'help', label: 'Handbuch', icon: '?' },
      { to: 'settings', label: 'Einstellungen', icon: '⚙' },
    ],
  },
];

/** Routenteil -> Beschriftung, für Meldungen außerhalb der Seitenleiste. */
export const AREA_LABELS: Record<string, string> = Object.fromEntries(
  NAVIGATION.flatMap((group) => group.items.map((item) => [item.to, item.label])),
);
